/**
 * @description Employee selection screen for the kiosk
 *
 * Key features:
 * - Lists active employees with first + last name
 * - Search/filter by name
 * - Sorted alphabetically A-Z, with PIN not set users at bottom
 * - Large touch targets for tablet kiosk
 * - Shows PIN status indicator (PIN not set = contact manager)
 * - Company name displayed in header
 * - Contractors filtered out by backend (not shown)
 */

import React, { useState, useEffect } from 'react';
import { Search, User } from 'lucide-react';
import { useClockContext } from '../../contexts/ClockContext';
import clockService from '../../services/clockService';

const EmployeeSelect = ({ companyName, onSelect }) => {
  const { companyId } = useClockContext();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const data = await clockService.getEmployees(companyId);
        setEmployees(data);
        setError(null);
      } catch (err) {
        setError('Failed to load employees');
      } finally {
        setLoading(false);
      }
    };
    loadEmployees();
  }, [companyId]);

  // Filter by search, then sort: A-Z alphabetically, PIN not set users at bottom
  const filteredEmployees = employees
    .filter((emp) => {
      if (!search) return true;
      const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
      return fullName.includes(search.toLowerCase());
    })
    .sort((a, b) => {
      // PIN not set users go to bottom
      if (a.hasPin && !b.hasPin) return -1;
      if (!a.hasPin && b.hasPin) return 1;
      // Then sort alphabetically by first name, then last name
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      {/* Header */}
      <header className="px-6 pt-8 pb-4 text-center">
        <h1 className="text-2xl font-bold text-white mb-1">{companyName || 'Time Clock'}</h1>
        <p className="text-slate-400">Select your name to clock in</p>
      </header>

      {/* Search */}
      <div className="px-6 pb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-primary-500 text-lg"
          />
        </div>
      </div>

      {/* Employee List */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {error && (
          <div className="text-center py-8 text-red-400">{error}</div>
        )}

        {!error && filteredEmployees.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            {search ? 'No employees match your search' : 'No employees found'}
          </div>
        )}

        <div className="space-y-2">
          {filteredEmployees.map((emp) => {
            const canSelect = emp.hasPin;

            return (
              <button
                key={emp.id}
                onClick={() => canSelect ? onSelect(emp) : null}
                disabled={!canSelect}
                className={`
                  w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-colors text-left
                  ${canSelect
                    ? 'bg-slate-800 hover:bg-slate-700 active:bg-slate-600 cursor-pointer'
                    : 'bg-slate-800/50 cursor-not-allowed opacity-60'
                  }
                `}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-slate-700">
                  <User className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">
                    {emp.firstName} {emp.lastName}
                  </p>
                  {!emp.hasPin && (
                    <p className="text-xs text-slate-500">PIN not set — contact your manager</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmployeeSelect;
